import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowRight, Building2, Check, CheckCheck, ChevronLeft, ChevronRight, CreditCard, Lock, Megaphone, Pause, Play, RotateCcw, Smartphone, X,
} from 'lucide-react';
import { CATEGORIES, PRICING_TIMELINE, findMarket, formatRate } from './pricingModel';

// AUTO-WA-39: a short animated walkthrough of how Meta charges for WhatsApp.
// Each slide is a scene built from HTML/CSS (no video), replayed on every visit,
// and still when the viewer prefers reduced motion.

const SLIDE_SECONDS = 10;

const reducedMotion = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
};

function useCountUp(target, { duration = 2400, delay = 400, run = true } = {}) {
  const [value, setValue] = useState(run && !reducedMotion() ? 0 : target);
  useEffect(() => {
    if (!run || reducedMotion()) { setValue(target); return undefined; }
    let frame;
    let start;
    const timer = setTimeout(() => {
      const step = (time) => {
        start = start ?? time;
        const progress = Math.min(1, (time - start) / duration);
        setValue(Math.round(target * (1 - (1 - progress) ** 3)));
        if (progress < 1) frame = requestAnimationFrame(step);
      };
      frame = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(timer); cancelAnimationFrame(frame); };
  }, [target, duration, delay, run]);
  return value;
}

/* ---------------------------------- Scenes --------------------------------- */

function ScenePerMessage({ utility, marketing }) {
  return (
    <div className="mbs-phone" aria-hidden="true">
      <div className="mbs-phone-top"><Smartphone size={13} />Your customer’s WhatsApp</div>
      <div className="mbs-bubble is-out d1"><span>Your order #1042 is packed.</span><em><CheckCheck size={13} />Delivered</em><b className="mbs-coin d1c">{utility}</b></div>
      <div className="mbs-bubble is-out d2"><span>Diwali offer: 20% off sweets!</span><em><CheckCheck size={13} />Delivered</em><b className="mbs-coin d2c">{marketing}</b></div>
      <div className="mbs-bubble is-out is-failed d3"><span>Your appointment is tomorrow.</span><em><X size={13} />Not delivered</em><b className="mbs-nocharge d3c">No charge</b></div>
    </div>
  );
}

function SceneKinds({ market }) {
  return (
    <div className="mbs-kinds" aria-hidden="true">
      {CATEGORIES.map((category, index) => (
        <div key={category.id} className="mbs-kind" style={{ '--c': category.color, animationDelay: `${0.25 + index * 0.35}s` }}>
          <span className="mbs-kind-label">{category.label}</span>
          <p>{category.example}</p>
          <b>{market ? `${formatRate(market[category.rate], 'INR')} each` : category.short}</b>
        </div>
      ))}
    </div>
  );
}

function SceneWindow() {
  return (
    <div className="mbs-window" aria-hidden="true">
      <div className="mbs-window-in"><span className="mbs-dot is-in" />Customer writes to you</div>
      <div className="mbs-track">
        <div className="mbs-track-fill" />
        <span className="mbs-reply r1" /><span className="mbs-reply r2" /><span className="mbs-reply r3" />
        <span className="mbs-lock"><Lock size={14} /></span>
      </div>
      <div className="mbs-track-scale"><span>0h</span><span>12h</span><span>24h</span></div>
      <div className="mbs-window-legend">
        <span><i className="is-open" />Inside the window: type any reply</span>
        <span className="mbs-after"><i className="is-closed" />After 24 hours: only approved templates</span>
      </div>
      <p className="mbs-window-note">Every new message from the customer starts the 24 hours again.</p>
    </div>
  );
}

function SceneNewRules({ rate, active }) {
  const count = useCountUp(1000, { duration: 2600, delay: 500, run: active });
  const over = count >= 1000;
  return (
    <div className="mbs-free" aria-hidden="true">
      <div className="mbs-free-count"><b className="ws-num">{count.toLocaleString('en-IN')}</b><span>free replies this month</span></div>
      <div className={`mbs-free-meter${over ? ' is-full' : ''}`}><i style={{ width: `${count / 10}%` }} /></div>
      <div className={`mbs-free-next${over ? ' is-on' : ''}`}>
        <span>Reply 1,001 onwards</span>
        <b>{rate} each</b>
        <i className="mbs-coin-rain"><em /><em /><em /></i>
      </div>
      <ul className="mbs-chips">
        <li>Per WhatsApp number</li><li>Resets on the 1st</li><li>Emoji reactions stay free</li><li>Needs a payment method</li>
      </ul>
    </div>
  );
}

function SceneUtility({ rate }) {
  return (
    <div className="mbs-before-after" aria-hidden="true">
      <div className="mbs-ba is-before">
        <small>Until 30 Sep 2026</small>
        <strong>Order update inside the 24h window</strong>
        <b className="mbs-strike">Free</b>
      </div>
      <ArrowRight className="mbs-ba-arrow" size={22} />
      <div className="mbs-ba is-after">
        <small>From 1 Oct 2026</small>
        <strong>Order update inside the 24h window</strong>
        <b>{rate}</b>
      </div>
      <p className="mbs-ba-note">Outside the window utility templates were always charged. Now they are charged everywhere.</p>
    </div>
  );
}

function SceneAds() {
  return (
    <div className="mbs-ads" aria-hidden="true">
      <div className="mbs-ad">
        <span className="mbs-ad-media"><Megaphone size={22} /></span>
        <strong>Sharma Sweets</strong><small>Sponsored · Fresh kaju katli today</small>
        <span className="mbs-ad-btn">Send message</span>
      </div>
      <ArrowRight className="mbs-ads-arrow" size={20} />
      <div className="mbs-ads-chat">
        <span className="mbs-mini in">Hi! Is it available?</span>
        <span className="mbs-mini out">Yes! 1 kg is ₹1,100.</span>
        <span className="mbs-mini out">Here’s our menu 🍬</span>
        <b className="mbs-ribbon">Up to 7 days · every message free</b>
      </div>
    </div>
  );
}

const COUNTRY_ROWS = [
  { code: '+91', market: 'India' },
  { code: '+971', market: 'United Arab Emirates' },
  { code: '+44', market: 'United Kingdom' },
  { code: '+1', market: 'North America' },
  { code: '+49', market: 'Germany' },
];

function SceneCountries({ card }) {
  return (
    <div className="mbs-countries" aria-hidden="true">
      <div className="mbs-country is-head"><span>Customer’s number</span><span>Marketing</span><span>Utility</span></div>
      {COUNTRY_ROWS.map((row, index) => {
        const market = findMarket(card, 'INR', row.market);
        return (
          <div key={row.code} className="mbs-country" style={{ animationDelay: `${0.3 + index * 0.3}s` }}>
            <span><b>{row.code}</b>{row.market}</span>
            <span className="ws-num">{market ? formatRate(market.marketing, 'INR') : '—'}</span>
            <span className="ws-num">{market ? formatRate(market.utility, 'INR') : '—'}</span>
          </div>
        );
      })}
      <p className="mbs-country-note">Same message, different price: it’s the customer’s country code that counts, not yours.</p>
    </div>
  );
}

function SceneVolume() {
  const steps = [0, 5, 10, 15, 20, 25];
  return (
    <div className="mbs-stairs" aria-hidden="true">
      {steps.map((discount, index) => (
        <div key={discount} className="mbs-stair" style={{ '--h': `${38 + index * 11}%`, animationDelay: `${0.2 + index * 0.25}s` }}>
          <b>{discount ? `−${discount}%` : 'List'}</b>
          <span>Level {index + 1}</span>
        </div>
      ))}
      <p className="mbs-stairs-note">An example: most countries step down 5% at a time (India up to 30%). Utility and authentication only; levels reset each month and count all your WhatsApp accounts together.</p>
    </div>
  );
}

function SceneWhoBills() {
  return (
    <div className="mbs-flow" aria-hidden="true">
      <div className="mbs-node"><Smartphone size={20} /><strong>You send</strong><small>through Auto Appy</small></div>
      <span className="mbs-wire"><i /></span>
      <div className="mbs-node is-meta"><Building2 size={20} /><strong>Meta delivers</strong><small>and counts each message</small></div>
      <span className="mbs-wire"><i /></span>
      <div className="mbs-node is-card"><CreditCard size={20} /><strong>Meta bills you</strong><small>card in Billing Hub</small></div>
    </div>
  );
}

const SAVE_TIPS = [
  'Reply within 24 hours so you can answer freely',
  'Send marketing only to people who asked for it',
  'Use utility templates for real updates, not offers',
  'Run Click-to-WhatsApp ads for 7 free days of chat',
  'Watch your 1,000 free replies on Your spend',
  'Keep a payment method saved in Billing Hub',
];

function SceneSave() {
  return (
    <ul className="mbs-checklist" aria-hidden="true">
      {SAVE_TIPS.map((tip, index) => (
        <li key={tip} style={{ animationDelay: `${0.3 + index * 0.45}s` }}><span className="mbs-check" style={{ animationDelay: `${0.55 + index * 0.45}s` }}><Check size={13} /></span>{tip}</li>
      ))}
    </ul>
  );
}

/* ---------------------------------- Slides --------------------------------- */

export const SLIDES = [
  {
    id: 'per-message', kicker: 'The basics', title: 'Meta charges for each message that is delivered',
    body: 'You pay Meta per delivered template message. A message that never reaches the phone costs nothing. The price depends on the kind of message and the customer’s country.',
    Scene: ({ india }) => <ScenePerMessage utility={india ? formatRate(india.utility, 'INR') : 'Utility'} marketing={india ? formatRate(india.marketing, 'INR') : 'Marketing'} />,
  },
  {
    id: 'kinds', kicker: 'Four kinds', title: 'Four kinds of messages, four prices',
    body: 'Meta puts every template in a category when it approves it. Marketing costs the most. Utility and authentication are cheap. Service replies are your normal chat messages. Prices shown are for customers in India.',
    Scene: ({ india }) => <SceneKinds market={india} />,
  },
  {
    id: 'window', kicker: 'The 24-hour window', title: 'When a customer writes, you get 24 hours to reply freely',
    body: 'Inside those 24 hours you can type any reply, send photos and documents. After that you can only send an approved template until the customer writes again.',
    Scene: () => <SceneWindow />,
  },
  {
    id: 'new-rules', kicker: 'New from 1 October 2026', title: 'Replies are free for the first 1,000 a month',
    body: 'Each WhatsApp number gets 1,000 free delivered replies every month. After that, each reply costs the same as a utility message. Unused free replies don’t carry over. Without a payment method, Meta stops delivering replies after the free 1,000.',
    Scene: ({ india, active }) => <SceneNewRules active={active} rate={india ? formatRate(india.service, 'INR') : 'Utility rate'} />,
  },
  {
    id: 'utility-change', kicker: 'Also from 1 October 2026', title: 'Order updates inside the window now cost money too',
    body: 'Since July 2025 a utility template sent while the 24-hour window was open was free. From 1 October 2026 every delivered utility template is charged, wherever it is sent.',
    Scene: ({ india }) => <SceneUtility rate={india ? `${formatRate(india.utility, 'INR')} each` : 'Utility rate'} />,
  },
  {
    id: 'ads', kicker: 'A free window', title: 'Chats from WhatsApp ads are free for up to 7 days',
    body: 'When someone taps a Click-to-WhatsApp ad on their phone and you reply in time, every message in that chat is free for up to 7 days, templates included.',
    Scene: () => <SceneAds />,
  },
  {
    id: 'countries', kicker: 'Country matters', title: 'The customer’s country sets the price',
    body: 'Meta prices by the country code of the number you message. Customers abroad usually cost more. See every country on Prices & estimate.',
    Scene: ({ card }) => <SceneCountries card={card} />,
  },
  {
    id: 'volume', kicker: 'Bulk discounts', title: 'Send a lot, pay less per message',
    body: 'When you send many utility or authentication messages to one country in a month, Meta lowers the price for the rest of that month. Most small businesses stay at the list price.',
    Scene: () => <SceneVolume />,
  },
  {
    id: 'who-bills', kicker: 'Who you pay', title: 'Meta bills you directly',
    body: 'Meta charges the payment method on your WhatsApp Business account in Meta’s Billing Hub. These charges are separate from your Auto Appy plan. Businesses billed in India must move to rupee (INR) billing by 31 December 2026.',
    Scene: () => <SceneWhoBills />,
  },
  {
    id: 'save', kicker: 'Keep it low', title: 'Six habits that keep costs low',
    body: 'Most of the bill comes from marketing and from replies beyond the free 1,000. These habits keep both under control and keep your number healthy.',
    Scene: () => <SceneSave />,
  },
];

export default function PricingStory({ card }) {
  const [params, setParams] = useSearchParams();
  const initial = Math.max(0, SLIDES.findIndex((slide) => slide.id === params.get('slide')));
  const [index, setIndex] = useState(initial);
  const [playing, setPlaying] = useState(() => !reducedMotion() && !params.get('slide'));
  const [hold, setHold] = useState(false);
  const [round, setRound] = useState(0);
  const timer = useRef(null);
  const slide = SLIDES[index];
  const india = useMemo(() => findMarket(card?.value, 'INR', 'India'), [card?.value]);

  const go = useCallback((next) => {
    const bounded = (next + SLIDES.length) % SLIDES.length;
    setIndex(bounded);
    setRound((value) => value + 1);
    setParams({ slide: SLIDES[bounded].id }, { replace: true });
  }, [setParams]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (!playing || hold) return undefined;
    timer.current = setTimeout(() => {
      if (index === SLIDES.length - 1) setPlaying(false);
      else go(index + 1);
    }, SLIDE_SECONDS * 1000);
    return () => clearTimeout(timer.current);
  }, [playing, hold, index, go, round]);

  const onKey = (event) => {
    if (event.key === 'ArrowRight') { event.preventDefault(); go(index + 1); }
    if (event.key === 'ArrowLeft') { event.preventDefault(); go(index - 1); }
  };

  const Scene = slide.Scene;
  return (
    <div className="mb-story-wrap">
      <section
        className={`ws-card mb-story${playing && !hold ? ' is-playing' : ''}`}
        aria-roledescription="carousel"
        aria-label="How Meta pricing works"
        onKeyDown={onKey}
        onMouseEnter={() => setHold(true)}
        onMouseLeave={() => setHold(false)}
        onFocus={() => setHold(true)}
        onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setHold(false); }}
      >
        <div className="mb-story-progress" aria-hidden="true">
          {SLIDES.map((item, position) => (
            <span key={item.id} className={position < index ? 'is-done' : position === index ? 'is-now' : ''}>
              <i key={`${round}-${position}`} style={position === index ? { animationDuration: `${SLIDE_SECONDS}s` } : undefined} />
            </span>
          ))}
        </div>

        <div key={`${slide.id}-${round}`} className="mb-slide" role="group" aria-roledescription="slide" aria-label={`${index + 1} of ${SLIDES.length}: ${slide.title}`}>
          <div className="mb-slide-copy">
            <span className="mb-kicker">{slide.kicker}</span>
            <h2>{slide.title}</h2>
            <p>{slide.body}</p>
          </div>
          <div className="mb-slide-scene">
            <Scene india={india} card={card?.value} active />
          </div>
        </div>

        <div className="mb-story-controls">
          <button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={() => go(index - 1)} aria-label="Previous slide"><ChevronLeft size={16} /></button>
          <button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={() => setPlaying((value) => !value)} aria-label={playing ? 'Pause walkthrough' : 'Play walkthrough'}>
            {playing ? <Pause size={15} /> : <Play size={15} />}{playing ? 'Pause' : 'Play'}
          </button>
          <button type="button" className="ws-btn ws-btn--plain ws-btn--sm" onClick={() => setRound((value) => value + 1)} aria-label="Replay this slide"><RotateCcw size={14} /></button>
          <span className="mb-story-count ws-num">{index + 1} / {SLIDES.length}</span>
          <div className="mb-story-dots" role="tablist" aria-label="Slides">
            {SLIDES.map((item, position) => (
              <button key={item.id} type="button" role="tab" aria-selected={position === index} aria-label={item.title} className={position === index ? 'is-active' : ''} onClick={() => go(position)} />
            ))}
          </div>
          <button type="button" className="ws-btn ws-btn--primary ws-btn--sm" onClick={() => go(index + 1)}>
            {index === SLIDES.length - 1 ? 'Start again' : 'Next'}<ChevronRight size={16} />
          </button>
        </div>
      </section>

      <aside className="mb-side">
        <section className="ws-card ws-card-pad">
          <h2 className="ws-h3">What changed and when</h2>
          <ol className="mb-timeline">
            {PRICING_TIMELINE.map((item) => (
              <li key={item.date}>
                <time dateTime={item.date}>{new Date(`${item.date}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</time>
                <strong>{item.title}</strong>
                <p>{item.text}</p>
              </li>
            ))}
          </ol>
          <p className="wa-help">Meta can change prices only on 1 January, 1 April, 1 July and 1 October, with at least a month’s notice.</p>
        </section>
        <section className="ws-card ws-card-pad mb-side-cta">
          <strong>What would this cost you?</strong>
          <p className="ws-muted">Enter how many messages you send and see a monthly estimate with today’s rates.</p>
          <Link className="ws-btn ws-btn--primary ws-btn--sm" to="/meta-billing/prices">Estimate my costs<ArrowRight size={14} /></Link>
        </section>
      </aside>
    </div>
  );
}
